import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { onboardingCopy } from "@/lib/onboarding-copy";
import { supabase } from "@/integrations/supabase/client";
import { setOnboardingOrigin } from "@/lib/onboarding-origin";
import { filterHealthAreasForProfessionalOnboarding } from "@/hooks/useHealthAreas";
import { PROFESSIONAL_ONBOARDING_ACCOUNT_TYPE } from "@/lib/onboarding/onboarding.schemas";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    healthArea: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [allHealthAreas, setAllHealthAreas] = useState<
    { id: string; name: string }[]
  >([]);
  const [loadingAreas, setLoadingAreas] = useState(true);

  const healthAreasForSelect = useMemo(
    () => filterHealthAreasForProfessionalOnboarding(allHealthAreas),
    [allHealthAreas],
  );

  // Carregar áreas de saúde do banco
  useEffect(() => {
    loadHealthAreas();
  }, []);

  const loadHealthAreas = async () => {
    try {
      const { data } = await supabase
        .from("health_areas")
        .select("id, name")
        .eq("is_active", true)
        .order("display_order");
      setAllHealthAreas(data || []);
    } catch (error) {
      console.error("Erro ao carregar áreas:", error);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Preencher formulário com dados da landing page
  useEffect(() => {
    const state = location.state as {
      fullName?: string;
      name?: string;
      email?: string;
      healthArea?: string;
    } | null;
    if (state) {
      setFormData((prev) => ({
        ...prev,
        name: state.fullName || state.name || prev.name,
        email: state.email || prev.email,
        healthArea: state.healthArea || prev.healthArea,
      }));
    }
  }, [location]);

  useEffect(() => {
    if (loadingAreas || healthAreasForSelect.length === 0) return;
    setFormData((prev) => {
      if (!prev.healthArea) return prev;
      const ok = healthAreasForSelect.some((a) => a.id === prev.healthArea);
      return ok ? prev : { ...prev, healthArea: "" };
    });
  }, [healthAreasForSelect, loadingAreas]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.password ||
      !formData.healthArea
    ) {
      toast.error(onboardingCopy.register.errors.fillAll);
      return;
    }

    if (formData.password.length < 8) {
      toast.error(onboardingCopy.register.errors.password);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(onboardingCopy.register.errors.email);
      return;
    }

    setIsLoading(true);

    try {
      const onboardingOrigin = PROFESSIONAL_ONBOARDING_ACCOUNT_TYPE;
      const emailRedirectTo = `${window.location.origin}/auth/verify-email?origin=${onboardingOrigin}`;

      // Criar usuário no Supabase (sem confirmação automática)
      const { data: authData, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo,
          data: {
            full_name: formData.name,
            health_area_id: formData.healthArea,
            account_type: PROFESSIONAL_ONBOARDING_ACCOUNT_TYPE,
          },
        },
      });

      if (error) {
        toast.error(error.message);
        setIsLoading(false);
        return;
      }

      if (!authData.user) {
        toast.error("Erro ao criar usuário");
        setIsLoading(false);
        return;
      }

      const accessToken =
        authData.session?.access_token ??
        (await supabase.auth.getSession()).data.session?.access_token;

      if (accessToken) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const emailResponse = await fetch(
          `${supabaseUrl}/functions/v1/send-confirmation-email`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: anonKey,
            },
            body: JSON.stringify({
              user_id: authData.user.id,
              email: formData.email,
              name: formData.name,
              origin: PROFESSIONAL_ONBOARDING_ACCOUNT_TYPE,
            }),
          },
        );

        const emailData = await emailResponse.json();

        if (!emailResponse.ok || emailData.error) {
          console.error("Erro ao enviar email:", emailData.error);
          toast.error(
            "Conta criada, mas houve erro ao enviar email de confirmação. Tente fazer login e reenviar.",
          );
        }
      } else {
        toast.error(
          "Conta criada, mas não há sessão ativa para enviar o e-mail de confirmação. Faça login e use reenviar na tela de confirmação.",
        );
      }

      // Salvar email no localStorage para a página de confirmação
      localStorage.setItem("pending_email_confirmation", formData.email);
      setOnboardingOrigin(onboardingOrigin);

      // Redirecionar para página de confirmação
      toast.success("Conta criada! Verifique seu e-mail para confirmar.");
      navigate(
        `/auth/confirm-email?email=${encodeURIComponent(
          formData.email,
        )}&origin=${onboardingOrigin}`,
      );
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(onboardingCopy.general.error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-8" />
        </div>

        <Card className="shadow-medium">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-primary mb-2">
                {onboardingCopy.register.title}
              </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {onboardingCopy.register.fields.name.label}
                </Label>
                <Input
                  id="name"
                  placeholder={onboardingCopy.register.fields.name.placeholder}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  {onboardingCopy.register.fields.email.label}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={onboardingCopy.register.fields.email.placeholder}
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {onboardingCopy.register.fields.password.label}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={
                    onboardingCopy.register.fields.password.placeholder
                  }
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="healthArea">
                  {onboardingCopy.register.fields.healthArea.label}
                </Label>
                <Select
                  value={formData.healthArea}
                  onValueChange={(value) =>
                    setFormData({ ...formData, healthArea: value })
                  }
                  disabled={loadingAreas || healthAreasForSelect.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingAreas
                          ? "Carregando..."
                          : onboardingCopy.register.fields.healthArea
                              .placeholder
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {healthAreasForSelect.map((area) => (
                      <SelectItem key={area.id} value={area.id}>
                        {area.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? onboardingCopy.general.saving : "Criar conta"}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                {onboardingCopy.general.lgpd}
              </p>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <a
            href="https://www.elinia.ai"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Voltar à página principal
          </a>
        </div>
      </div>
    </div>
  );
};

export default Register;
